import React, { useState } from 'react';
import s from './s.module.less';

type Props = {
    onSelect: (method: 'invite' | 'create') => void;
};

type PermissionCategory = {
    title: string;
    items: string[];
};

const PROVIDER_PERMISSIONS: PermissionCategory[] = [
    {
        title: 'Patient care',
        items: ['Patient information', 'Patient communications', 'Patient reviews', 'Schedule & edit appointments'],
    },
    {
        title: 'Clinical care',
        items: ['Visit notes', 'Prescriptions', 'Text visits'],
    },
    {
        title: 'Billing & payments',
        items: ['Invoices'],
    },
    {
        title: 'Practice management',
        items: [
            'Homepage',
            'Practice profile',
            'Service templates',
            'Medications settings',
            'Marketing (Pro-listings)',
            'Appointment policies (No-shows, late cancellations and rescheduling)',
        ],
    },
    {
        title: 'Team & access',
        items: ['Add providers', 'Settings'],
    },
];

const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6L8 10L12 6" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PermissionPanel: React.FC<{ category: PermissionCategory }> = ({ category }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className={s.permCategory}>
            <div className={s.permHeader} onClick={() => setOpen(!open)}>
                <span className={s.permTitle}>{category.title}</span>
                <span className={`${s.permChevron} ${open ? s.permChevronOpen : ''}`}>
                    <ChevronDownIcon />
                </span>
            </div>
            {open && (
                <div className={s.permItems}>
                    {category.items.map((item) => (
                        <div key={item} className={s.permItem}>{item}</div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ChooseMethodStep: React.FC<Props> = ({ onSelect }) => {
    return (
        <div className={s.body}>
            <div className={s.section}>
                <h3 className={s.sectionTitle}>How do you want to add new providers?</h3>
                <div className={s.optionCard} onClick={() => onSelect('invite')}>
                    <div className={s.optionTitle}>Invite providers to create their own profile</div>
                    <div className={s.optionDesc}>Provider will fill out their information themselves</div>
                </div>
                <div className={s.optionCard} onClick={() => onSelect('create')}>
                    <div className={s.optionTitle}>Create profiles for providers</div>
                    <div className={s.optionDesc}>
                        You create provider profiles first. Providers will review and complete missing information.
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ChooseUsageStep: React.FC<{ onSelect: (ehrOnly: boolean) => void }> = ({ onSelect }) => {
    return (
        <div className={s.body}>
            <div className={s.section}>
                <h3 className={s.sectionTitle}>What will the new providers use?</h3>
                <div className={s.optionCard} onClick={() => onSelect(true)}>
                    <div className={s.optionTitle}>The EHR only</div>
                    <div className={s.optionDesc}>Providers get an account to see patients, write notes and schedule visits.</div>
                </div>
                <div className={s.optionCard} onClick={() => onSelect(false)}>
                    <div className={s.optionTitle}>The EHR and list on Klarity Health</div>
                    <div className={s.optionDesc}>
                        Providers get an account, plus a public profile patients can find and book on helloklarity.com.
                        You’ll choose who fills in the profile next.
                    </div>
                </div>
            </div>
            <div className={s.divider} />
            <div className={s.section}>
                <h3 className={s.sectionTitle}>Provider’s permission by category</h3>
                <div className={s.permList}>
                    {PROVIDER_PERMISSIONS.map((cat) => (
                        <PermissionPanel key={cat.title} category={cat} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ChooseMethodStep;
